import { useState, useCallback, useMemo, useEffect } from "react"

import { kebabCase } from "lodash"
import { Button, cn, FormFeedback, FormGroup, SimpleAlert, LoadingSpinner, Label, Input, Select, Row, Col } from "twyne-new.ui"

export default function Form({ data, version, versions = [], onSubmit, onCancel }) {
  const [from, setFrom] = useState(null)
  const [description, setDescription] = useState("")
  const [slug, setSlug] = useState("")

  const [error, setError] = useState({})
  const [loading, setLoading] = useState(false)
  const [slugTouch, setSlugTouch] = useState(false)

  const fromOption = useMemo(() => [
    { label: "Default Version", value: "default" },
    { label: "Selected Version", value: "selected" },
  ].filter(({ value }) => value !== "selected" || version), [version])

  useEffect(() => {
    if (!from) {
      setFrom(version ? "selected" : "default")
    }
  }, [version, from])

  const validate = () => {
    const errors = {}
    if (!description.trim()) {
      errors.description = "Description is required"
    }
    if (!slug.trim()) {
      errors.slug = "Slug is required"
    } else if (versions.some(entry => entry.slug?.toLowerCase() === slug.trim().toLowerCase())) {
      errors.slug = "This slug is already in use"
    }
    setError(errors)
    return !Object.keys(errors).length
  }

  const handleSubmit = async () => {
    if (!validate()) {
      return
    }

    setLoading(true)
    try {
      await onSubmit({ description, slug, data })
    } catch (e) {
      setError({ submit: "Version was not created. Please try again." })
    } finally {
      setLoading(false)
    }
  }

  const parseSlug = useCallback(value => {
    value = `${value}`
      .replace(/[^a-zA-Z0-9\-_.~ ]/g, '')
    return kebabCase(value)
  }, [])

  const handleFrom = useCallback(option => {
    const { value } = option
    setFrom(value)
  }, [])

  const handleDescription = useCallback(event => {
    const { value } = event.target
    setDescription(value)
    setError({})
    if (!slugTouch) {
      setSlug(parseSlug(value))
    }
  }, [slugTouch, parseSlug])

  const handleSlug = useCallback(event => {
    const { value } = event.target
    setSlugTouch(true)
    setSlug(value)
    setError({})
  }, [])

  return <Row>
    <Col size="12">
      <FormGroup>
        <Label>From</Label>
        <Select name="from" value={from} options={fromOption} onChange={handleFrom} disabled />
      </FormGroup>
    </Col>
    <Col size="12">
      <FormGroup className="relative">
        <Label>Description</Label>
        <Input name="description" value={description} onChange={handleDescription} error={error.description} />
        {error.description && <FormFeedback className="absolute left-0 top-full mt-1">{error.description}</FormFeedback>}
      </FormGroup>
    </Col>
    <Col size="12">
      <FormGroup className="relative">
        <Label>Slug</Label>
        <Input name="slug" value={slug} onChange={handleSlug} error={error.slug} />
        {error.slug && <FormFeedback className="absolute left-0 top-full mt-1">{error.slug}</FormFeedback>}
      </FormGroup>
    </Col>
    {error.submit ? <Col size="12">
      <SimpleAlert className="m-0 py-2 px-3" variant="danger">{error.submit}</SimpleAlert>
    </Col> : null}
    <Col size="12">
      <div className={cn("flex gap-2", !onCancel && "justify-end")}>
        <Button onClick={handleSubmit} className="flex items-center" color="accent" disabled={loading}>
          {loading ? <LoadingSpinner width={18} height={18} /> : "Save"}
        </Button>
        {onCancel && <Button color="neutral" variant="ghost" onClick={onCancel}>Cancel</Button>}
      </div>
    </Col>
  </Row>
}
